import React, { useEffect, useState, useRef } from 'react';
import { Loader2, Heart, Trash2, ChevronRight, Calendar, MapPin, Trophy, Users, ArrowLeft, Shirt } from 'lucide-react';
import { favoriteService } from '../../services/favoriteService';
import { fetchTeamInfo, fetchTeamRecentMatches, fetchTeamSquad } from '../../services/footballService';
import { Language, Game } from '../../types';
import { t } from '../../i18n';

interface FavoriteTeamDetailViewProps {
    teamId: string;
    lang: Language;
}

export const FavoriteTeamDetailView: React.FC<FavoriteTeamDetailViewProps> = ({ teamId, lang }) => {
    const [selectedTeam, setSelectedTeam] = useState<any | null>(null);
    const [recentMatches, setRecentMatches] = useState<Game[]>([]);
    const [squad, setSquad] = useState<{ [key: string]: any[] }>({});
    const [loading, setLoading] = useState(true);
    const [matchesLoading, setMatchesLoading] = useState(false);
    const [squadLoading, setSquadLoading] = useState(false);

    const scrollRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    const handleMouseDown = (e: React.MouseEvent, pos: string) => {
        const slider = scrollRefs.current[pos];
        if (!slider) return;
        isDragging.current = true;
        startX.current = e.pageX - slider.offsetLeft;
        scrollLeft.current = slider.scrollLeft;
        slider.style.cursor = 'grabbing';
        slider.style.userSelect = 'none';
    };

    const handleMouseLeave = (pos: string) => {
        const slider = scrollRefs.current[pos];
        if (!slider) return;
        isDragging.current = false;
        slider.style.cursor = 'grab';
    };

    const handleMouseUp = (pos: string) => {
        const slider = scrollRefs.current[pos];
        if (!slider) return;
        isDragging.current = false;
        slider.style.cursor = 'grab';
    };

    const handleMouseMove = (e: React.MouseEvent, pos: string) => {
        if (!isDragging.current) return;
        e.preventDefault();
        const slider = scrollRefs.current[pos];
        if (!slider) return;
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - startX.current) * 2;
        slider.scrollLeft = scrollLeft.current - walk;
    };

    const loadTeamData = async () => {
        setLoading(true);
        try {
            const info = await fetchTeamInfo(teamId);
            setSelectedTeam(info);
            
            if (info) {
                setMatchesLoading(true);
                setSquadLoading(true);
                
                const [matches, squadData] = await Promise.all([
                    fetchTeamRecentMatches(teamId, 5),
                    fetchTeamSquad(teamId)
                ]);
                
                setRecentMatches(matches);
                
                // Group by position
                const grouped: { [key: string]: any[] } = {
                    [t(lang, 'goalkeeper')]: [],
                    [t(lang, 'defender')]: [],
                    [t(lang, 'midfielder')]: [],
                    [t(lang, 'attacker')]: []
                };
                
                squadData.forEach((p: any) => {
                    const pos = p.position.toLowerCase();
                    if (pos.includes('goalkeeper')) grouped[t(lang, 'goalkeeper')].push(p);
                    else if (pos.includes('defender')) grouped[t(lang, 'defender')].push(p);
                    else if (pos.includes('midfielder')) grouped[t(lang, 'midfielder')].push(p);
                    else if (pos.includes('attacker') || pos.includes('striker')) grouped[t(lang, 'attacker')].push(p);
                    else grouped[t(lang, 'attacker')].push(p);
                });
                
                setSquad(grouped);
                setMatchesLoading(false);
                setSquadLoading(false);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (teamId) {
            loadTeamData();
        }
    }, [teamId]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 className="animate-spin text-rose-500" />
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse">
                    {t(lang, 'syncingMatchIntelDesc')}
                </span>
            </div>
        );
    }

    if (!selectedTeam) {
        return (
            <div className="flex flex-col items-center justify-center py-32 px-6 text-center gap-4">
                <p className="text-sm font-bold text-slate-400">{t(lang, 'teamNotFound')}</p>
                <button onClick={() => window.navigateTo('/mypage/teams')} className="px-6 py-2 bg-slate-900 text-white rounded-full text-xs font-black uppercase">{t(lang, 'goBack')}</button>
            </div>
        );
    }

    return (
        <div className="animate-in fade-in slide-in-from-right duration-500 pb-20">
            {/* Team Header Detail - Improved for Light UI */}
            <div className="bg-white p-8 rounded-b-[3.5rem] relative overflow-hidden shadow-sm border-b border-slate-100">
                <div className="flex flex-col items-center mt-4">
                    <div className="w-28 h-28 p-5 bg-white rounded-[2rem] mb-6 shadow-xl border border-slate-50 relative z-10">
                        <img src={selectedTeam.team.logo} alt="" className="w-full h-full object-contain" />
                    </div>
                    <h2 className="text-2xl font-black uppercase tracking-tight text-center text-slate-900">
                        {selectedTeam.team.name}
                    </h2>
                    <div className="flex items-center gap-2 mt-2 text-slate-400 font-bold text-xs uppercase tracking-widest bg-slate-50 px-4 py-1.5 rounded-full">
                        <MapPin size={12} className="text-rose-500" />
                        {selectedTeam.venue.city}, {selectedTeam.team.country}
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-10">
                    <div className="bg-slate-50/80 rounded-3xl p-4 flex flex-col items-center border border-slate-100/50">
                        <Trophy size={18} className="text-amber-500 mb-2" />
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t(lang, 'founded')}</span>
                        <span className="text-sm font-black mt-1 text-slate-900">{selectedTeam.team.founded}</span>
                    </div>
                    <div className="bg-slate-50/80 rounded-3xl p-4 flex flex-col items-center border border-slate-100/50">
                        <Users size={18} className="text-indigo-500 mb-2" />
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t(lang, 'capacity')}</span>
                        <span className="text-sm font-black mt-1 text-slate-900">{(selectedTeam.venue.capacity / 1000).toFixed(1)}k</span>
                    </div>
                    <div className="bg-slate-50/80 rounded-3xl p-4 flex flex-col items-center border border-slate-100/50">
                        <Calendar size={18} className="text-rose-500 mb-2" />
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t(lang, 'season')}</span>
                        <span className="text-sm font-black mt-1 text-slate-900">2024/25</span>
                    </div>
                </div>
            </div>

            <div className="max-w-[700px] mx-auto">
                {/* Venue Info */}
                <div className="px-6 mt-10">
                    <div className="flex items-center justify-between mb-6 px-2">
                        <h3 className="text-xs font-black text-slate-900 uppercase tracking-[0.2em]">{t(lang, 'homeStadium')}</h3>
                        <span className="w-8 h-1 bg-slate-900 rounded-full"></span>
                    </div>
                    <div className="bg-slate-50/50 rounded-[2.5rem] p-6 border border-slate-100 flex items-center gap-6">
                        <div className="w-20 h-20 rounded-2xl overflow-hidden shrink-0 border border-white shadow-md">
                            <img src={selectedTeam.venue.image} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div>
                            <p className="font-black text-slate-900 text-lg tracking-tight leading-tight">{selectedTeam.venue.name}</p>
                            <p className="text-[11px] font-bold text-slate-400 uppercase mt-1 tracking-wider">{selectedTeam.venue.city}, {selectedTeam.venue.address}</p>
                        </div>
                    </div>
                </div>

                {/* Squad List Section - NEW */}
                <div className="px-6 mt-12">
                    <div className="flex items-center justify-between mb-6 px-2">
                        <div className="flex items-center gap-3">
                            <Shirt size={18} className="text-slate-900" />
                            <h3 className="text-xs font-black text-slate-900 uppercase tracking-[0.2em]">{t(lang, 'squad')}</h3>
                        </div>
                        <span className="w-8 h-1 bg-slate-900 rounded-full"></span>
                    </div>

                    {squadLoading ? (
                        <div className="flex justify-center py-10">
                            <Loader2 className="animate-spin text-rose-500" />
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {Object.entries(squad).map(([position, players]) => (
                                players.length > 0 && (
                                    <div key={position}>
                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] mb-4 px-2 flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                                            {position}
                                        </h4>
                                        <div 
                                            ref={el => { scrollRefs.current[position] = el; }}
                                            onMouseDown={(e) => handleMouseDown(e, position)}
                                            onMouseLeave={() => handleMouseLeave(position)}
                                            onMouseUp={() => handleMouseUp(position)}
                                            onMouseMove={(e) => handleMouseMove(e, position)}
                                            className="flex overflow-x-auto pb-4 gap-3 no-scrollbar -mx-2 px-2 cursor-grab active:cursor-grabbing"
                                        >
                                            {players.map((player) => (
                                                <div 
                                                    key={player.id}
                                                    onClick={() => window.navigateTo(`/player/${player.id}`)}
                                                    className="min-w-[140px] max-w-[140px] bg-white rounded-2xl p-4 border border-slate-100 flex flex-col items-center text-center hover:border-rose-200 transition-all cursor-pointer group shadow-sm active:scale-95"
                                                >
                                                    <div className="w-16 h-16 rounded-full bg-slate-50 overflow-hidden border-2 border-slate-100 mb-3 group-hover:border-rose-100 transition-colors">
                                                        <img 
                                                            src={player.photo} 
                                                            alt={player.name} 
                                                            className="w-full h-full object-cover"
                                                            onError={(e: any) => { e.target.src = 'https://media.api-sports.io/football/players/not-found.png' }}
                                                        />
                                                    </div>
                                                    <div className="w-full">
                                                        <p className="text-[11px] font-black text-slate-900 truncate group-hover:text-rose-500 transition-colors">{player.name}</p>
                                                        <div className="flex items-center justify-center gap-1.5 mt-1">
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase">#{player.number || '-'}</span>
                                                            <span className="w-1 h-1 rounded-full bg-slate-200"></span>
                                                            <span className="text-[9px] font-black text-rose-500 uppercase">{player.age}y</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            ))}
                        </div>
                    )}
                </div>

                {/* Recent Matches */}
                <div className="px-6 mt-12 mb-10">
                    <div className="flex items-center justify-between mb-6 px-2">
                        <h3 className="text-xs font-black text-slate-900 uppercase tracking-[0.2em]">{t(lang, 'recentMatches')}</h3>
                        <span className="w-8 h-1 bg-slate-900 rounded-full"></span>
                    </div>

                    {matchesLoading ? (
                        <div className="flex justify-center py-10">
                            <Loader2 className="animate-spin text-rose-500" />
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {recentMatches.map((match) => (
                                <div 
                                    key={match.id}
                                    onClick={() => window.navigateTo(`/game/${match.id}`)}
                                    className="bg-white rounded-3xl p-5 border border-slate-100 flex items-center justify-between shadow-sm hover:border-slate-200 transition-all cursor-pointer group active:scale-[0.98]"
                                >
                                    <div className="flex items-center gap-4 w-[40%]">
                                        <div className="w-10 h-10 p-2 bg-slate-50 rounded-xl border border-slate-100 shrink-0 group-hover:bg-white transition-colors">
                                            <img src={match.homeTeam.logo} alt="" className="w-full h-full object-contain" />
                                        </div>
                                        <span className="text-[11px] font-black text-slate-900 uppercase truncate tracking-tight">{match.homeTeam.name}</span>
                                    </div>

                                    <div className="flex flex-col items-center w-[20%]">
                                        <div className="text-sm font-black text-slate-900 tracking-tighter tabular-nums bg-slate-50 px-3 py-1 rounded-full group-hover:bg-slate-100 transition-colors">
                                            {match.score}
                                        </div>
                                        <span className="text-[8px] font-black text-slate-300 uppercase mt-1 tracking-widest">{match.status === 'Finished' ? 'FT' : match.status}</span>
                                    </div>

                                    <div className="flex items-center gap-4 justify-end w-[40%] text-right">
                                        <span className="text-[11px] font-black text-slate-900 uppercase truncate tracking-tight">{match.awayTeam.name}</span>
                                        <div className="w-10 h-10 p-2 bg-slate-50 rounded-xl border border-slate-100 shrink-0 group-hover:bg-white transition-colors">
                                            <img src={match.awayTeam.logo} alt="" className="w-full h-full object-contain" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
